import { Archive } from '@untitledui/icons';

import type { ProposalDetail } from '@/lib/proposals/core/types';
import { getWithdrawalExplanation } from '@/lib/proposals/core/withdrawalReasons';

import { DetailPanelCard } from './shared/DetailPanelCard';

interface WithdrawalExplanationProps {
  executionResult: ProposalDetail['execution_result'];
}

/**
 * Detail-pane card explaining why the SYSTEM withdrew (archived) a
 * proposal. Renders human copy mapped from execution_result.reason via
 * getWithdrawalExplanation — the raw agent-authored reason is never shown.
 * Caller gates on resolution === 'withdrawn'.
 *
 * Deliberately mirrors the Action Overview card's anatomy (DetailPanelCard
 * container, small leading icon in an auto column, title beside it, body
 * aligned under the title) rather than the settings-page StatusBanner —
 * the jumbo FeaturedIcon treatment read as foreign next to sibling cards.
 */
export function WithdrawalExplanation({
  executionResult,
}: WithdrawalExplanationProps) {
  const explanation = getWithdrawalExplanation(executionResult);
  // Mirrors the content column below (ProposalContent: `p-3 sm:p-4 lg:p-6`
  // + `max-w-[1000px] mx-auto`) so the card's edges line up exactly with the
  // sibling cards. No bottom padding: the content area's own top padding
  // provides the gap beneath, keeping one consistent vertical rhythm.
  return (
    <div role='status' className='px-3 pt-3 sm:px-4 sm:pt-4 lg:px-6 lg:pt-6'>
      <div className='mx-auto w-full max-w-[1000px]'>
        <DetailPanelCard>
          <div className='grid grid-cols-[auto_1fr] gap-x-2.5 px-4 py-4'>
            <Archive
              aria-hidden='true'
              className='size-5 flex-shrink-0 self-center text-fg-tertiary'
              strokeWidth={2}
            />
            <span className='text-sm font-semibold text-[var(--color-text-secondary)]'>
              Archived
            </span>
            {explanation && (
              <p className='col-start-2 pt-1 text-sm leading-6 text-[var(--color-text-tertiary)]'>
                {explanation}
              </p>
            )}
          </div>
        </DetailPanelCard>
      </div>
    </div>
  );
}
